import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconDragDrop } from '@iwoca/icons';
import clsx from 'clsx';
import styles from './SortableListItem.module.css';

type TItemProps = HTMLAttributes<HTMLDivElement> & {
  item: string;
  withOpacity?: boolean;
  isDragging?: boolean;
  itemRenderer?: (
    params: { item: string; isDragging: boolean },
    index: number,
  ) => ReactNode;
  attributes?: any;
  listeners?: any;
  ind: number;
};

export const SortableListItem = (
  props: Omit<TItemProps, 'attributes' | 'listeners'>,
) => {
  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.item });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  return (
    <Item
      ref={setNodeRef}
      style={style}
      withOpacity={isDragging}
      {...props}
      attributes={attributes}
      listeners={listeners}
    />
  );
};

export const Item = forwardRef<HTMLDivElement, TItemProps>(
  (
    {
      item,
      withOpacity,
      isDragging,
      style,
      itemRenderer,
      attributes,
      ind,
      listeners,
      ...props
    },
    ref,
  ) => {
    const classNames = clsx(styles.item, {
      [styles.isDragging]: Boolean(isDragging),
      [styles.withOpacity]: Boolean(withOpacity),
    });

    const dragButtonProps = { ...listeners, ...attributes };

    return (
      <div ref={ref} style={{ ...style }} className={classNames} {...props}>
        <button className={styles.dragButton} {...dragButtonProps}>
          <IconDragDrop className={styles.dragIcon} />
        </button>
        <div className={styles.content}>
          {itemRenderer?.({ item, isDragging: isDragging || false }, ind) ||
            item}
        </div>
      </div>
    );
  },
);

Item.displayName = 'Item';
